/**
 * @fileoverview useCalendar Hook
 * @description Custom hook for managing calendar data fetching and state
 */

import { useState, useCallback, useEffect } from 'react';
import { calendarApi } from '@/services/api/calendar';
import { useAuth } from '@/context';

/**
 * Custom hook for calendar events
 */
export const useCalendar = () => {
  const { getUserProfile } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Lookup data
  const [teams, setTeams] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [testTypes, setTestTypes] = useState([]);

  const user = getUserProfile();
  const userId = user?.email || user?.id || '';
  const userName = user?.name || '';

  /**
   * Fetch events with optional filters
   */
  const fetchEvents = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarApi.getEvents(filters);
      const payload = response?.data ?? response;
      const data = Array.isArray(payload) ? payload
        : Array.isArray(payload?.items) ? payload.items
        : [];
      setEvents(data);
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch events:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch lookup data (teams, environments, test types)
   */
  /**
   * Extract array from backend envelope: { success, data: { items: [...] } } or { data: [...] } or [...]
   */
  const unwrapList = (res) => {
    const payload = res?.data ?? res;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const fetchLookups = useCallback(async () => {
    try {
      const [teamsRes, envsRes, typesRes] = await Promise.all([
        calendarApi.getTeams(),
        calendarApi.getEnvironments(),
        calendarApi.getTestTypes(),
      ]);
      setTeams(unwrapList(teamsRes));
      setEnvironments(unwrapList(envsRes));
      setTestTypes(unwrapList(typesRes));
    } catch (err) {
      console.error('Failed to fetch lookups:', err);
    }
  }, []);

  /**
   * Create a new event
   */
  const createEvent = useCallback(async (eventData) => {
    const payload = {
      ...eventData,
      created_by: userId,
      created_by_name: userName,
    };
    const response = await calendarApi.createEvent(payload);
    await fetchEvents();
    return response?.data || response;
  }, [userId, userName, fetchEvents]);

  /**
   * Update an existing event
   */
  const updateEvent = useCallback(async (eventId, eventData) => {
    const payload = {
      ...eventData,
      updated_by: userId,
      updated_by_name: userName,
    };
    const response = await calendarApi.updateEvent(eventId, payload);
    await fetchEvents();
    return response?.data || response;
  }, [userId, userName, fetchEvents]);

  /**
   * Delete an event (soft delete)
   */
  const deleteEvent = useCallback(async (eventId) => {
    await calendarApi.deleteEvent(eventId, userId, userName);
    await fetchEvents();
  }, [userId, userName, fetchEvents]);

  /**
   * Change event status
   */
  const changeStatus = useCallback(async (eventId, newStatus) => {
    const response = await calendarApi.changeEventStatus(eventId, newStatus, userId, userName);
    await fetchEvents();
    return response?.data || response;
  }, [userId, userName, fetchEvents]);

  /**
   * Extend an event
   */
  const extendEvent = useCallback(async (eventId, newEndDate, reason) => {
    const response = await calendarApi.extendEvent(eventId, newEndDate, userId, userName, reason);
    await fetchEvents();
    return response?.data || response;
  }, [userId, userName, fetchEvents]);

  /**
   * Close an event (COMPLETED, CLOSED_EARLY, CANCELLED)
   */
  const closeEvent = useCallback(async (eventId, status, reason) => {
    const response = await calendarApi.closeEvent(eventId, status, userId, userName, reason);
    await fetchEvents();
    return response?.data || response;
  }, [userId, userName, fetchEvents]);

  /**
   * Check if current user is the owner of an event
   */
  const isEventOwner = useCallback((event) => {
    return event?.created_by === userId;
  }, [userId]);

  // Load lookup data on mount
  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  return {
    // State
    events,
    teams,
    environments,
    testTypes,
    loading,
    error,
    userId,
    userName,

    // Actions
    fetchEvents,
    fetchLookups,
    createEvent,
    updateEvent,
    deleteEvent,
    changeStatus,
    extendEvent,
    closeEvent,

    // Helpers
    isEventOwner,
  };
};

export default useCalendar;
